import React, { useState } from "react";
import { Text, Modal } from "src/ccl/document";
import { Button } from "src/ccl/navigation";
import { Box, Flex } from "src/ccl/layout";
import { User } from "src/graphql/types";
import { copyToClipboard } from "src/utils/clipboard";
import { pluralize } from "src/utils/lang";
import { baseUrl } from "src/config";

interface ShareShortlistModalProps {
  talent: User[];
  isOpen: boolean;
  onClose: () => void;
  // Only the legacy modal used the verticals for its copy; callers may still
  // pass them until U19 trims the call sites.
  talentVerticals?: string[];
}

export const ShareShortlistModal = ({
  talent,
  isOpen,
  onClose,
}: ShareShortlistModalProps) => {
  const [textHasBeenCopied, setTextHasBeenCopied] = useState(false);
  const talentClipped: string[] = [];
  const displayShortlistTalentClipboard = (talent: User[]) => {
    //make talent name and URL into string in an array for clipboard
    return talent.map((t) => {
      const url = `${baseUrl}/creatives/${t.slug}`;
      talentClipped.push(`${t.name} - ${url}`);

      return (
        <Flex
          key={t.id}
          css={{
            gap: "$4",
            overflow: "hidden",
            whiteSpace: "nowrap",
            "@bp2": { gap: "$2" },
          }}
        >
          <Text>{t.name}</Text>
          <Text>{url}</Text>
        </Flex>
      );
    });
  };

  const setupClipboard = () => {
    setTextHasBeenCopied(true);
    const clipped = talentClipped.join(`\n`);
    copyToClipboard(clipped);
  };

  // The rebranded modal is the only one now: it also serves the signed-out
  // create-job flow and opted-out users on legacy pages.
  return (
    <Modal
      title="Share your shortlist"
      isOpen={isOpen}
      showCloseButton
      onClose={onClose}
      width="wide"
      context="createJob"
      titleCss={{ textTransform: "none" }}
      isNewDashboard
    >
      <Text css={{ mb: "$9", mt: "$6" }}>
        We&apos;ve included links to the{" "}
        {pluralize(talent.length, "talent", "talents")} on your shortlist below.
        Share with your team to get feedback.
      </Text>

      <Text weight="bold" css={{ fontFamily: "$sansNew" }}>
        Shortlist
      </Text>
      <Box
        css={{
          border: "2px solid $grey3",
          borderRadius: "$lg",
          p: "$4",
          minHeight: "124px",
        }}
      >
        {talent && displayShortlistTalentClipboard(talent)}
      </Box>
      <Flex
        css={{
          mt: "$9",
          justifyContent: "flex-start",
          flexDirection: "column-reverse",
          "@bp1": { justifyContent: "space-between", flexDirection: "row" },
        }}
      >
        <Button
          variant="secondaryV2"
          onClick={onClose}
          css={{
            mr: "$0",
            padding: "21px 24px",
            "@bp2": { width: "initial", mr: "$4" },
          }}
        >
          Cancel
        </Button>

        <Button
          variant="primaryV2"
          onClick={() => {
            setupClipboard();
          }}
          css={{
            mb: "$5",
            padding: "21px 24px",
            "@bp1": { width: "initial", mb: 0 },
            minWidth: "$18",
          }}
        >
          <Box css={{ "@bp1": { display: "none" } }}>
            {textHasBeenCopied ? "Copied!" : "Copy"}
          </Box>
          <Box css={{ display: "none", "@bp1": { display: "block" } }}>
            {textHasBeenCopied ? "Copied!" : "Copy to clipboard"}
          </Box>
        </Button>
      </Flex>
    </Modal>
  );
};
